import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../../services/toastService'
import { adminAPI, uploadAPI } from '../../services/api'
import { createLogger } from '../../utils/logger'
import { trackAdminSightingModerate } from '../../utils/analytics'

const logger = createLogger('SightingDetailActions')

/**
 * Custom hook for SightingDetail page actions
 * Extracts all handler logic to reduce component size
 * 
 * @param {Object} params
 * @param {string} params.sightingId - Current sighting ID
 * @param {Function} params.loadSighting - Function to reload sighting data
 * @param {Function} params.execute - useAsyncOperation execute function
 * @param {Object} params.formData - Form data state
 * @param {Function} params.setFormData - Form data setter
 * @param {Function} params.setMapPosition - Map position setter
 * @param {Function} params.setImagePreview - Image preview setter
 * @param {Function} params.setUploadingImage - Uploading image setter
 * @param {Function} params.setImageActionLoading - Image action loading setter
 * @param {Function} params.setUploadingImages - Uploading images setter
 * @param {Function} params.setEntitySearch - Entity search setter
 * @param {Function} params.setEntitySearchResults - Entity search results setter
 * @param {Function} params.setSelectedEntity - Selected entity setter
 * @param {Function} params.setShowEntitySearch - Show entity search setter
 * @returns {Object} Handler functions
 */
export function useSightingDetailActions({
  sightingId,
  loadSighting,
  execute,
  formData,
  setFormData,
  setMapPosition,
  setImagePreview,
  setUploadingImage,
  setImageActionLoading,
  setUploadingImages,
  setEntitySearch,
  setEntitySearchResults,
  setSelectedEntity,
  setShowEntitySearch
}) {
  const navigate = useNavigate()

  // Entity search handler
  const handleEntitySearch = useCallback(async (searchTerm) => {
    setEntitySearch(searchTerm)
    if (searchTerm.length < 2) {
      setEntitySearchResults([])
      return
    }

    try {
      const response = await adminAPI.getAllEntities({ search: searchTerm, limit: 10 })
      setEntitySearchResults(response.data.entities || [])
    } catch (error) {
      logger.error('Failed to search entities', error)
      toast.error('Failed to search entities')
    }
  }, [setEntitySearch, setEntitySearchResults])

  // Select entity from search
  const handleSelectEntity = useCallback((entity) => {
    setSelectedEntity(entity)
    setFormData(prev => ({ ...prev, entityId: entity.id }))
    setEntitySearch(entity.name)
    setShowEntitySearch(false)
  }, [setFormData, setSelectedEntity, setEntitySearch, setShowEntitySearch])

  // Clear selected entity
  const handleClearEntity = useCallback(() => {
    setSelectedEntity(null)
    setFormData(prev => ({ ...prev, entityId: '' }))
    setEntitySearch('')
  }, [setFormData, setSelectedEntity, setEntitySearch])

  // Map position change handler
  const handleMapPositionChange = useCallback((position) => {
    if (position) {
      setMapPosition(position)
      setFormData(prev => ({
        ...prev,
        latitude: position.lat,
        longitude: position.lng
      }))
    }
  }, [setFormData, setMapPosition])

  // Image selection and upload
  const handleImageSelect = useCallback(async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB')
      return
    }

    setImagePreview(URL.createObjectURL(file))
    setUploadingImage(true)

    try {
      const uploadResponse = await uploadAPI.uploadImage(file)
      setFormData(prev => ({ ...prev, imageUrl: uploadResponse.data.url }))
      toast.success('Image uploaded successfully')
    } catch (error) {
      logger.error('Failed to upload image', error)
      toast.error('Failed to upload image')
      setImagePreview(null)
    } finally {
      setUploadingImage(false)
    }
  }, [setFormData, setImagePreview, setUploadingImage])

  // Remove image preview
  const handleRemoveImage = useCallback(() => {
    setImagePreview(null)
    setFormData(prev => ({ ...prev, imageUrl: '' }))
  }, [setFormData, setImagePreview])

  // Submit new sighting
  const handleSubmitSighting = useCallback(async (e) => {
    e.preventDefault()

    await execute(
      async () => {
        const sightingData = {
          entityId: formData.entityId,
          latitude: formData.latitude,
          longitude: formData.longitude,
          address: formData.address || null,
          notes: formData.notes || null,
          imageUrl: formData.imageUrl || null
        }

        const response = await adminAPI.createSighting(sightingData)
        const newSightingId = response.data.data?.id || response.data.id

        toast.success('Sighting created successfully')
        navigate(`/admin/sightings/${newSightingId}`)
      },
      { errorMessage: 'Failed to create sighting' }
    )
  }, [execute, formData, navigate])

  // Approve/reject image
  const handleApproveImage = useCallback(async (imageId, isApproved) => {
    try {
      setImageActionLoading(imageId)
      const action = isApproved ? 'reject' : 'approve'
      await adminAPI.moderateImage(imageId, action)
      
      trackAdminSightingModerate(sightingId, action, true)
      
      toast.success(`Image ${action}d successfully`)
      await loadSighting()
    } catch (error) {
      logger.error('Failed to moderate image', error)
      toast.error('Failed to update image status')
    } finally {
      setImageActionLoading(null)
    }
  }, [sightingId, loadSighting, setImageActionLoading])

  // Set primary image
  const handleSetPrimaryImage = useCallback(async (imageId) => {
    try {
      setImageActionLoading(imageId)
      await adminAPI.setPrimaryImage(imageId)
      toast.success('Primary image updated successfully')
      await loadSighting()
    } catch (error) {
      logger.error('Failed to set primary image', error)
      toast.error('Failed to update primary image')
    } finally {
      setImageActionLoading(null)
    }
  }, [loadSighting, setImageActionLoading])

  // Moderate sighting (approve/reject)
  const handleModerateSighting = useCallback(async (action) => {
    await execute(
      async () => {
        await adminAPI.moderateSighting(sightingId, action)
        toast.success(`Sighting ${action}d successfully`)
        await loadSighting()
      },
      { errorMessage: 'Failed to moderate sighting' }
    )
  }, [execute, sightingId, loadSighting])

  // Delete image
  const handleDeleteImage = useCallback(async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return
    }
    
    try {
      setImageActionLoading(imageId)
      await adminAPI.deleteImage(imageId)
      toast.success('Image deleted successfully')
      await loadSighting()
    } catch (error) {
      logger.error('Failed to delete image', error)
      toast.error('Failed to delete image')
    } finally {
      setImageActionLoading(null)
    }
  }, [loadSighting, setImageActionLoading])

  // Upload new images to existing sighting
  const handleImageUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    const maxSize = 5 * 1024 * 1024
    const oversizedFiles = files.filter(file => file.size > maxSize)
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ')
      toast.error(`File(s) too large: ${fileNames}. Max 5MB per image.`)
      event.target.value = ''
      return
    }

    setUploadingImages(true)
    let successCount = 0
    let errorCount = 0

    for (const file of files) {
      try {
        const uploadResponse = await uploadAPI.uploadImage(file)
        await adminAPI.addImageToSighting(sightingId, { imageUrl: uploadResponse.data.url })
        successCount++
      } catch (error) {
        logger.error('Failed to upload image', error)
        errorCount++
      }
    }

    setUploadingImages(false)
    event.target.value = ''

    if (successCount > 0) {
      toast.success(`${successCount} image(s) uploaded successfully`)
      await loadSighting()
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} image(s) failed to upload`)
    }
  }, [sightingId, loadSighting, setUploadingImages])

  // Delete entire sighting
  const handleDeleteSighting = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this sighting? This action cannot be undone.')) {
      return
    }

    await execute(
      async () => {
        await adminAPI.deleteSighting(sightingId)
        toast.success('Sighting deleted successfully')
        navigate('/admin/sightings')
      },
      { errorMessage: 'Failed to delete sighting' }
    )
  }, [execute, sightingId, navigate])

  return {
    handleEntitySearch,
    handleSelectEntity,
    handleClearEntity,
    handleMapPositionChange,
    handleImageSelect,
    handleRemoveImage,
    handleSubmitSighting,
    handleApproveImage,
    handleSetPrimaryImage,
    handleModerateSighting,
    handleDeleteImage,
    handleImageUpload,
    handleDeleteSighting
  }
}
